import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPublishedResultInputSchema, type CreatePublishedResultInput, publishedRegimeSchema } from 'fuel-benchmark-contracts';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { FormField } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { Select, type SelectOption } from '../../components/ui/Select';
import { Sheet } from '../../components/ui/Sheet';
import { numberFieldOptions, serverMessage, submitErrorKey, textFieldOptions } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';

export interface PublishedResultSheetProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleId: string;
}

const DEFAULT_METRIC = 'fuel_consumption_combined';
const DEFAULT_UNIT = 'L/100km';

/** Records a WLTP/EPA/GCC figure against a vehicle. Never mixed into measured results (plan §3.3). */
export const PublishedResultSheet = ({ isOpen, onClose, vehicleId }: PublishedResultSheetProps) => {
  const { t } = useI18nTranslation();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const defaultValues: Partial<CreatePublishedResultInput> = {
    vehicleId,
    regime: 'GCC',
    metric: DEFAULT_METRIC,
    unit: DEFAULT_UNIT,
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreatePublishedResultInput>({
    resolver: zodResolver(createPublishedResultInputSchema),
    defaultValues,
  });

  useEffect(() => {
    if (isOpen) {
      setSubmitError(null);
      reset({ vehicleId, regime: 'GCC', metric: DEFAULT_METRIC, unit: DEFAULT_UNIT });
    }
  }, [isOpen, vehicleId, reset]);

  const createResult = useMutation(orpc.vehicles.publishedResults.create.mutationOptions());

  const regimeOptions: SelectOption[] = publishedRegimeSchema.options.map((value) => ({ value, label: t(`enums.regime.${value}`) }));

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);

    try {
      await createResult.mutateAsync({ ...values, vehicleId });
      await queryClient.invalidateQueries({ queryKey: orpc.vehicles.get.key() });
      onClose();
    } catch (error) {
      setSubmitError(serverMessage(error) ?? t(submitErrorKey(error)));
    }
  });

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      title={t('vehicles.published.add')}
      description={t('vehicles.published.subtitle')}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            type="submit"
            form="published-result-form"
            isLoading={isSubmitting}
          >
            {isSubmitting ? t('actions.saving') : t('actions.save')}
          </Button>
        </>
      }
    >
      <form
        id="published-result-form"
        noValidate
        onSubmit={onSubmit}
        className="space-y-4"
      >
        <FormField
          id="regime"
          label={t('vehicles.published.regime')}
          error={errors.regime?.message}
        >
          <Select
            id="regime"
            options={regimeOptions}
            hasError={Boolean(errors.regime)}
            {...register('regime')}
          />
        </FormField>

        <FormField
          id="metric"
          label={t('vehicles.published.metric')}
          error={errors.metric?.message}
        >
          <Input
            id="metric"
            className="font-mono"
            hasError={Boolean(errors.metric)}
            {...register('metric')}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            id="value"
            label={t('vehicles.published.value')}
            error={errors.value?.message}
          >
            <Input
              id="value"
              type="number"
              step="0.01"
              inputMode="decimal"
              hasError={Boolean(errors.value)}
              {...register('value', numberFieldOptions)}
            />
          </FormField>

          <FormField
            id="unit"
            label={t('conditions.protocols.columns.unit')}
            error={errors.unit?.message}
          >
            <Input
              id="unit"
              hasError={Boolean(errors.unit)}
              {...register('unit')}
            />
          </FormField>
        </div>

        <FormField
          id="source"
          label={t('vehicles.published.source')}
          error={errors.source?.message}
        >
          <Input
            id="source"
            hasError={Boolean(errors.source)}
            {...register('source', textFieldOptions)}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            id="sourceDate"
            label={t('vehicles.published.sourceDate')}
            error={errors.sourceDate?.message}
          >
            <Input
              id="sourceDate"
              type="date"
              hasError={Boolean(errors.sourceDate)}
              {...register('sourceDate', textFieldOptions)}
            />
          </FormField>

          <FormField
            id="market"
            label={t('vehicles.published.market')}
            error={errors.market?.message}
          >
            <Input
              id="market"
              hasError={Boolean(errors.market)}
              {...register('market', textFieldOptions)}
            />
          </FormField>
        </div>

        <FormField
          id="published-notes"
          label={t('vehicles.published.notes')}
          error={errors.notes?.message}
        >
          <textarea
            id="published-notes"
            rows={3}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
            {...register('notes', textFieldOptions)}
          />
        </FormField>

        {submitError ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {submitError}
          </p>
        ) : null}
      </form>
    </Sheet>
  );
};
